// HAND-ROLLED — documented exception per
// feedback_deterministic_codegen_over_hand_rolling.md. Same rationale as
// activity-feed-list.ts: audit logs have no entity-hint, just a
// cross-screen spec at specs/tier-05-experience/audit-logs.cross-screen.yaml.
//
// CHG-1643 / Wave 1 Tier 1 — port of v1 AuditLogs.tsx.

import type { ListPageConfig } from "../components/composition/ListPage";
import { auditLogsApi, type AuditLogEntry } from "../api/audit-logs";
import type { ReactNode } from "react";

function actionColumn(row: AuditLogEntry): ReactNode {
  return row.action.replace(/_/g, " ");
}

function userColumn(row: AuditLogEntry): ReactNode {
  return row.user_email ?? row.user_id ?? "system";
}

function resourceColumn(row: AuditLogEntry): ReactNode {
  const t = row.resource_type;
  if (!row.resource_id) return t;
  return `${t} ${row.resource_id.slice(0, 8)}…`;
}

export function buildAuditLogsListConfig(
  scope: "org" | "platform" = "org",
): ListPageConfig<AuditLogEntry> {
  const isPlatform = scope === "platform";
  return {
    title: isPlatform ? "Platform Audit Trail" : "Audit Trail",
    subtitle: isPlatform
      ? "Sensitive-action audit trail across every tenant on the platform."
      : "Every administrative action recorded in your organization.",
    queryKey: ["audit-logs", scope] as const,
    queryFn: ({ page, pageSize, search }) =>
      auditLogsApi.listAuditLogs({ page, pageSize, search, scope }),
    defaultSort: { field: "created_at", direction: "desc" },
    pageSize: 50,
    search: { enabled: true, placeholder: "Search descriptions, resource types, user emails…" },
    emptyState: {
      title: "No audit logs yet",
      subtitle: "Audit logs appear here as your team performs administrative actions. Every change is tracked for accountability.",
    },
    columns: [
      { field: "created_at", label: "When", type: "datetime", sortable: true },
      { field: "action", label: "Action", type: "custom", render: actionColumn },
      { field: "resource_type", label: "Resource", type: "custom", render: resourceColumn },
      { field: "description", label: "Detail", type: "text" },
      { field: "user_email", label: "User", type: "custom", render: userColumn },
      { field: "ip_address", label: "IP", type: "text" },
    ],
  };
}

export const auditLogsListConfig = buildAuditLogsListConfig("org");
export const platformAuditLogsListConfig = buildAuditLogsListConfig("platform");
